import { useEffect, useState } from "react";
import { flushSync } from "react-dom";
import { toast } from "sonner";
import { User, Bell, Lock, Smartphone, Trash2, LogOut } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useT } from "@/context/i18n";
import { useNavigate } from "react-router-dom";
import { Skeleton } from "@/components/common";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";

const mask = (m) => (m ? m.slice(0, 2) + "••••" + m.slice(-2) : "—");

function MobileVerifyModal({ open, onOpenChange, hasMobile, onDone }) {
  const [step, setStep] = useState("mobile");
  const [mobile, setMobile] = useState("");
  const [otp, setOtp] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [demoOtp, setDemoOtp] = useState(null);
  const [busy, setBusy] = useState(false);

  const resetState = () => { setStep("mobile"); setMobile(""); setOtp(""); setSessionId(null); setDemoOtp(null); setBusy(false); };
  const handleOpenChange = (v) => { if (!v) resetState(); onOpenChange(v); };

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(mobile)) { toast.error("Enter a valid 10-digit mobile number"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/profile/mobile/start", { mobile });
      setSessionId(data.session_id);
      setDemoOtp(data.demo_otp || null);
      setOtp("");
      setStep("otp");
      toast.success("OTP sent to your mobile");
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  const verifyOtp = async () => {
    if (otp.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setBusy(true);
    try {
      await api.post("/profile/mobile/verify", { session_id: sessionId, otp });
      toast.success(hasMobile ? "Mobile number updated" : "Mobile number added");
      resetState();
      onOpenChange(false);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    setBusy(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="mobile-verify-modal">
        <DialogHeader>
          <DialogTitle>{hasMobile ? "Update your mobile number" : "Add your mobile number"}</DialogTitle>
          <DialogDescription>
            {step === "mobile"
              ? "We'll send a one-time verification code to confirm this number."
              : `Enter the 6-digit code sent to +91 ${mobile}.`}
          </DialogDescription>
        </DialogHeader>

        {step === "mobile" ? (
          <div className="space-y-3">
            <div className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 focus-within:border-[#14B8A6]">
              <span className="shrink-0 text-sm font-semibold text-slate-500">+91</span>
              <input
                data-testid="mobile-input"
                type="tel"
                inputMode="numeric"
                autoFocus
                maxLength={10}
                value={mobile}
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
                onKeyDown={(e) => { if (e.key === "Enter") sendOtp(); }}
                placeholder="10-digit mobile number"
                className="min-w-0 flex-1 text-sm text-[#0B132B] outline-none"
              />
            </div>
            <DialogFooter>
              <button
                data-testid="mobile-send-otp"
                onClick={sendOtp}
                disabled={busy || mobile.length !== 10}
                className="w-full rounded-md bg-[#14B8A6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B132B] disabled:opacity-50">
                {busy ? "Sending…" : "Send OTP"}
              </button>
            </DialogFooter>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex justify-center">
              <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="mobile-otp-input">
                <InputOTPGroup>
                  {[0, 1, 2, 3, 4, 5].map((i) => (
                    <InputOTPSlot key={i} index={i} className="h-11 w-11 text-base" />
                  ))}
                </InputOTPGroup>
              </InputOTP>
            </div>
            {demoOtp && (
              <p className="text-center text-xs text-slate-400" data-testid="mobile-demo-otp">Your OTP: <span className="font-mono font-bold text-[#14B8A6]">{demoOtp}</span></p>
            )}
            <DialogFooter className="flex-col gap-2 sm:flex-col">
              <button
                data-testid="mobile-verify-otp"
                onClick={verifyOtp}
                disabled={busy || otp.length !== 6}
                className="w-full rounded-md bg-[#14B8A6] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B132B] disabled:opacity-50">
                {busy ? "Verifying…" : "Verify & save"}
              </button>
              <button
                data-testid="mobile-resend-otp"
                onClick={sendOtp}
                disabled={busy}
                className="w-full text-center text-xs font-semibold text-slate-500 hover:text-[#0B132B] disabled:opacity-50">
                Didn't get it? Resend OTP
              </button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Profile() {
  const { logout } = useAuth();
  const t = useT();
  const nvg = useNavigate();
  const [p, setP] = useState(null);
  const [name, setName] = useState("");
  const [mobileModal, setMobileModal] = useState(false);
  useEffect(() => { api.get("/profile").then((r) => { setP(r.data); setName(r.data.name || ""); }).catch(() => {}); }, []);
  const saveName = async () => {
    const v = name.trim();
    if (!v) { toast.error("Name cannot be empty"); return; }
    try { await api.put("/profile", { name: v }); setP({ ...p, name: v }); toast.success("Profile updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const save = async (patch) => {
    try { await api.put("/profile", patch); setP({ ...p, ...patch }); toast.success("Preferences updated"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const del = async () => {
    try { await api.delete("/profile"); toast.success("Account deletion requested"); flushSync(() => logout()); nvg("/", { replace: true }); }
    catch (e) { toast.error(apiError(e)); }
  };
  if (!p) return <div className="space-y-4"><Skeleton className="h-40" /><Skeleton className="h-40" /></div>;
  const card = "rounded-xl border border-slate-200 bg-white p-5 shadow-sm";
  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div><h1 className="font-display text-2xl font-bold text-[#0B132B]">Profile & Settings</h1><p className="text-sm text-slate-500">Manage your personal details, preferences and security.</p></div>
      {!p.mobile && <div data-testid="add-mobile-banner" className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">Add your mobile number to complete your profile. <button data-testid="add-mobile-now" onClick={() => setMobileModal(true)} className="font-bold underline">Add now</button></div>}

      <div className={card} data-testid="profile-edit-card">
        <div className="flex items-center gap-4">
          <span className="grid h-14 w-14 shrink-0 place-items-center rounded-full bg-[#0B132B] text-xl font-bold text-white">{(p.name || "U")[0]}</span>
          <div className="min-w-0 flex-1">
            <label className="text-xs font-semibold text-slate-400">{t("Full Name")}</label>
            <div className="mt-1 flex gap-2">
              <input data-testid="profile-name-input" value={name} onChange={(e) => setName(e.target.value)}
                className="min-w-0 flex-1 rounded-md border border-slate-200 px-3 py-2 text-sm text-[#0B132B] outline-none focus:border-[#14B8A6]" />
              <button data-testid="profile-name-save" onClick={saveName} disabled={!name.trim() || name.trim() === p.name}
                className="shrink-0 rounded-md bg-[#14B8A6] px-4 py-2 text-sm font-semibold text-white hover:bg-[#0B132B] disabled:opacity-50">{t("Save")}</button>
            </div>
            <p className="mt-1 truncate text-sm text-slate-500">{p.email}</p>
          </div>
        </div>
        <div className="mt-4 grid gap-3 border-t border-slate-100 pt-4 sm:grid-cols-2 text-sm">
          <div><p className="text-xs text-slate-400">{t("Mobile (masked)")}</p><p className="font-medium text-[#0B132B]">{mask(p.mobile)}</p></div>
        </div>
      </div>

      <div className={card}>
        <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><Bell size={16} /> {t("Communication Preferences")}</h3>
        <div className="mt-3 space-y-2">
          {[["comm_email", "Email updates"], ["comm_sms", "SMS reminders"], ["comm_whatsapp", "WhatsApp reminders"]].map(([k, l]) => (
            <label key={k} className="flex items-center justify-between rounded-md bg-slate-50 px-4 py-3 text-sm">
              <span className="font-medium text-[#0B132B]">{t(l)}</span>
              <input type="checkbox" data-testid={`pref-${k}`} checked={!!p[k]} onChange={(e) => save({ [k]: e.target.checked })} className="h-4 w-4 accent-[#14B8A6]" />
            </label>
          ))}
        </div>
      </div>

      <div className={card}>
        <h3 className="flex items-center gap-2 font-display font-bold text-[#0B132B]"><Lock size={16} /> {t("Security")}</h3>
        <div className="mt-3 space-y-2 text-sm">
          <button onClick={() => setMobileModal(true)} className="flex w-full items-center gap-2 rounded-md border border-slate-200 px-4 py-3 font-medium text-[#0B132B] hover:bg-slate-50" data-testid="change-mobile"><Smartphone size={16} /> {t("Update your Mobile number")}</button>
          <button onClick={() => { flushSync(() => logout()); nvg("/", { replace: true }); }} className="flex w-full items-center gap-2 rounded-md border border-slate-200 px-4 py-3 font-medium text-[#0B132B] hover:bg-slate-50" data-testid="profile-logout"><LogOut size={16} /> {t("Log out of this session")}</button>
        </div>
      </div>

      <div className="rounded-xl border border-red-200 bg-red-50/40 p-5">
        <h3 className="flex items-center gap-2 font-display font-bold text-red-600"><Trash2 size={16} /> {t("Delete Account")}</h3>
        <p className="mt-1 text-sm text-slate-600">This will request permanent deletion of your account and data. This action cannot be undone.</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <button data-testid="delete-account" className="mt-3 rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700">{t("Delete my account")}</button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader><AlertDialogTitle>Delete your account?</AlertDialogTitle><AlertDialogDescription>This requests permanent deletion of your ZRo Pe account. Your loans and payment history will no longer be accessible.</AlertDialogDescription></AlertDialogHeader>
            <AlertDialogFooter><AlertDialogCancel data-testid="delete-cancel">Cancel</AlertDialogCancel><AlertDialogAction onClick={del} data-testid="delete-confirm" className="bg-red-600 hover:bg-red-700">Yes, delete</AlertDialogAction></AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>

      <MobileVerifyModal open={mobileModal} onOpenChange={setMobileModal} hasMobile={!!p.mobile} onDone={() => window.location.reload()} />
    </div>
  );
}
